import { WbsItemAdditionalPropertyValueType } from '.'
import { intl } from '../../../i18n'
import { parseFullNumberToHalf } from '../../../utils/number'
import {
  WbsItemAdditionalPropertyEntity,
  WbsItemAdditionalPropertyType,
} from '../../entity/WbsItemAdditionalPropertyEntity'

type ValidationErrorMessage = string
type ValidattionResult = ValidationErrorMessage | undefined
type PartialPropertyConfiguration = {
  required: boolean | undefined
}
export const validateAdditionalPropertyValue = (
  additionalProperty: WbsItemAdditionalPropertyEntity,
  value: WbsItemAdditionalPropertyValueType,
  propertyConfiguration?: PartialPropertyConfiguration
): ValidattionResult => {
  switch (additionalProperty.propertyType) {
    case WbsItemAdditionalPropertyType.NUMBER:
      return validateNumber(additionalProperty, value, propertyConfiguration)
    case WbsItemAdditionalPropertyType.TEXT:
      return validateText(additionalProperty, value, propertyConfiguration)
    case WbsItemAdditionalPropertyType.MULTI_LINE_TEXT:
      return validateMultiLineText(
        additionalProperty,
        value,
        propertyConfiguration
      )
    case WbsItemAdditionalPropertyType.DATE:
      return validateDate(additionalProperty, value, propertyConfiguration)
    case WbsItemAdditionalPropertyType.DATE_TIME:
      return validateDateTime(additionalProperty, value, propertyConfiguration)
    case WbsItemAdditionalPropertyType.CHECKBOX:
      return validateCheckbox(additionalProperty, value, propertyConfiguration)
    case WbsItemAdditionalPropertyType.SELECT:
      return validateSelect(additionalProperty, value, propertyConfiguration)
    case WbsItemAdditionalPropertyType.MULTI_SELECT:
      return validateMultiSelect(
        additionalProperty,
        value,
        propertyConfiguration
      )
    case WbsItemAdditionalPropertyType.ENTITY_SEARCH:
      return validateEntitySearch(
        additionalProperty,
        value,
        propertyConfiguration
      )
    default:
      return undefined
  }
}
const validateNumber = (
  additionalProperty: WbsItemAdditionalPropertyEntity,
  value: WbsItemAdditionalPropertyValueType,
  propertyConfiguration?: PartialPropertyConfiguration
): ValidattionResult => {
  if (
    required(additionalProperty, propertyConfiguration) &&
    !value &&
    value !== 0
  ) {
    return intl.formatMessage({ id: 'boolExpression.requiredField' })
  }
  if (value === undefined || value === null) {
    return
  }
  let numberValue: number | undefined = undefined
  if (typeof value === 'number') {
    numberValue = value
  } else if (typeof value === 'string') {
    try {
      const numberString = parseFullNumberToHalf(value)
      if (numberString) {
        numberValue = Number(numberString)
        if (Number.isNaN(numberValue)) {
          return intl.formatMessage({ id: 'validator.invalidNumber' })
        }
      }
    } catch (err) {
      // Ignore
      return intl.formatMessage({ id: 'validator.invalidNumber' })
    }
  } else {
    return intl.formatMessage({ id: 'validator.invalidNumber' })
  }
  return undefined
}
const validateText = (
  additionalProperty: WbsItemAdditionalPropertyEntity,
  value: WbsItemAdditionalPropertyValueType,
  propertyConfiguration?: PartialPropertyConfiguration
): ValidattionResult => {
  if (required(additionalProperty, propertyConfiguration) && !value) {
    return intl.formatMessage({ id: 'boolExpression.requiredField' })
  }
  if (value && typeof value === 'string' && value.length > 10000) {
    return intl.formatMessage(
      { id: 'validator.character.maxLength' },
      { maxLength: 10000 }
    )
  }
  return undefined
}
const validateMultiLineText = (
  additionalProperty: WbsItemAdditionalPropertyEntity,
  value: WbsItemAdditionalPropertyValueType,
  propertyConfiguration?: PartialPropertyConfiguration
): ValidattionResult => {
  if (required(additionalProperty, propertyConfiguration) && !value) {
    return intl.formatMessage({ id: 'boolExpression.requiredField' })
  }
  if (value && typeof value === 'string' && value.length > 10000) {
    return intl.formatMessage(
      { id: 'validator.character.maxLength' },
      { maxLength: 10000 }
    )
  }
  return undefined
}
const validateDate = (
  additionalProperty: WbsItemAdditionalPropertyEntity,
  value: WbsItemAdditionalPropertyValueType,
  propertyConfiguration?: PartialPropertyConfiguration
): ValidattionResult => {
  if (required(additionalProperty, propertyConfiguration) && !value) {
    return intl.formatMessage({ id: 'boolExpression.requiredField' })
  }
  return undefined
}
const validateDateTime = (
  additionalProperty: WbsItemAdditionalPropertyEntity,
  value: WbsItemAdditionalPropertyValueType,
  propertyConfiguration?: PartialPropertyConfiguration
): ValidattionResult => {
  if (required(additionalProperty, propertyConfiguration) && !value) {
    return intl.formatMessage({ id: 'boolExpression.requiredField' })
  }
  return undefined
}
const validateCheckbox = (
  additionalProperty: WbsItemAdditionalPropertyEntity,
  value: WbsItemAdditionalPropertyValueType,
  propertyConfiguration?: PartialPropertyConfiguration
): ValidattionResult => {
  return undefined
}
const validateSelect = (
  additionalProperty: WbsItemAdditionalPropertyEntity,
  value: WbsItemAdditionalPropertyValueType,
  propertyConfiguration?: PartialPropertyConfiguration
): ValidattionResult => {
  if (required(additionalProperty, propertyConfiguration) && !value) {
    return intl.formatMessage({ id: 'boolExpression.requiredField' })
  }
  if (
    !additionalProperty.selectOptions ||
    additionalProperty.selectOptions.length === 0
  ) {
    return
  }
  if (value && !additionalProperty.selectOptions.some(v => value === v.uuid)) {
    return intl.formatMessage({ id: 'validator.invalidSelectOption' })
  }
  return undefined
}
const validateMultiSelect = (
  additionalProperty: WbsItemAdditionalPropertyEntity,
  value: WbsItemAdditionalPropertyValueType,
  propertyConfiguration?: PartialPropertyConfiguration
): ValidattionResult => {
  if (
    required(additionalProperty, propertyConfiguration) &&
    (!value || (Array.isArray(value) && value.length === 0))
  ) {
    return intl.formatMessage({ id: 'boolExpression.requiredField' })
  }
  if (
    !additionalProperty.selectOptions ||
    additionalProperty.selectOptions.length === 0
  ) {
    return
  }
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return
  }
  if (
    Array.isArray(value) &&
    value.length > 0 &&
    !additionalProperty.selectOptions.some(v => value.includes(v.uuid))
  ) {
    return intl.formatMessage({ id: 'validator.invalidSelectOption' })
  }
  return undefined
}
const validateEntitySearch = (
  additionalProperty: WbsItemAdditionalPropertyEntity,
  value: WbsItemAdditionalPropertyValueType,
  propertyConfiguration?: PartialPropertyConfiguration
): ValidattionResult => {
  if (required(additionalProperty, propertyConfiguration) && !value) {
    return intl.formatMessage({ id: 'boolExpression.requiredField' })
  }
  return undefined
}

const required = (
  additionalProperty: WbsItemAdditionalPropertyEntity,
  propertyConfiguration?: PartialPropertyConfiguration
) => {
  if (
    propertyConfiguration &&
    typeof propertyConfiguration.required === 'boolean'
  ) {
    return propertyConfiguration.required
  }
  return additionalProperty.required
}
